class ProductMediaLightbox {
    constructor() {
        this.lightbox = document.getElementById("productLightbox");
        this.lightboxImage = document.getElementById("lightboxImage");
        this.lightboxCounter = document.getElementById("lightboxCounter");
        this.currentIndex = 0;
        this.allMediaItems = [];
        this.mediaItems = [];
        this.thumbnails = [];
        this.mainImage = null;
        this.variantData = null;
        this.imageCache = new Map();
        this.isTransitioning = false;

        if (!this.lightbox) return;
        this.init();
    }

    init() {
        this.collectMediaItems();
        this.cacheElements();
        this.setupEventListeners();
        this.setupMobileGestures();
        this.setupThumbnailClickHandlers();
    }

    cacheElements() {
        this.mainImage = document.querySelector(".product-gallery__primary-image");
        this.thumbnails = [...document.querySelectorAll(".product-gallery__thumbnail")];
    }

    collectMediaItems() {
        const mediaDataScript = document.getElementById("productMediaData");
        if (mediaDataScript) {
            try {
                const mediaData = JSON.parse(mediaDataScript.textContent);
                this.allMediaItems = mediaData.images.map((img) => ({
                    fullSrc: img.src,
                    alt: img.alt,
                    id: img.id,
                    variants: img.variants || [],
                }));
                this.variantData = mediaData.variants || [];
                this.mediaItems = [...this.allMediaItems];
            } catch (error) {
                this.fallbackToVisibleImages();
            }
        } else {
            this.fallbackToVisibleImages();
        }
    }

    fallbackToVisibleImages() {
        const images = document.querySelectorAll(".product-gallery__image[data-full-src]");
        this.mediaItems = Array.from(images).map((img) => ({
            fullSrc: img.dataset.fullSrc,
            alt: img.alt,
            id: img.dataset.mediaId,
        }));
    }

    preloadImage(src) {
        if (this.imageCache.has(src)) return Promise.resolve();

        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                this.imageCache.set(src, img);
                resolve();
            };
            img.onerror = reject;
            img.src = src;
        });
    }

    async switchToImageSmooth(index) {
        if (index < 0 || index >= this.mediaItems.length || this.isTransitioning) return;

        this.isTransitioning = true;
        this.currentIndex = index;

        const currentMedia = this.mediaItems[index];
        const baseSrc = currentMedia.fullSrc.replace(/width=\d+/, '');
        const newSrc = `${baseSrc}width=1000`;

        if (this.mainImage) {
            this.mainImage.classList.add('is-changing');

            try {
                await this.preloadImage(newSrc);

                setTimeout(() => {
                    this.updateMainImageDirect(currentMedia);
                    this.mainImage.classList.remove('is-changing');
                    this.isTransitioning = false;
                }, 150);
            } catch {
                this.mainImage.classList.remove('is-changing');
                this.isTransitioning = false;
            }
        }

        this.updateActiveThumbnail();
    }

    switchToImage(index) {
        this.switchToImageSmooth(index);
    }

    updateMainImageDirect(currentMedia) {
        if (!currentMedia || !this.mainImage) return;

        const baseSrc = currentMedia.fullSrc.replace(/width=\d+/, '');

        this.mainImage.src = `${baseSrc}width=1000`;
        this.mainImage.srcset = `
            ${baseSrc}width=400 400w,
            ${baseSrc}width=600 600w,
            ${baseSrc}width=800 800w,
            ${baseSrc}width=1000 1000w,
            ${baseSrc}width=1200 1200w,
            ${baseSrc}width=1600 1600w,
            ${baseSrc}width=2000 2000w,
            ${baseSrc}width=2400 2400w
        `.trim();
        this.mainImage.alt = currentMedia.alt;
        this.mainImage.dataset.mediaId = currentMedia.id;
    }

    updateMainImage() {
        const currentMedia = this.mediaItems[this.currentIndex];
        if (currentMedia) {
            this.updateMainImageDirect(currentMedia);
        }
    }

    setThumbnailState(thumbnail, state) {
        thumbnail.classList.remove('is-loading', 'is-hidden');
        if (state === 'loading') thumbnail.classList.add('is-loading');
        if (state === 'hidden') thumbnail.classList.add('is-hidden');
    }

    updateActiveThumbnail() {
        this.thumbnails.forEach((thumbnail) => {
            const thumbnailIndex = parseInt(thumbnail.getAttribute('data-thumbnail-index'));
            const isActive = !isNaN(thumbnailIndex) && thumbnailIndex === this.currentIndex;

            thumbnail.classList.toggle("product-gallery__thumbnail--active", isActive);

            if (isActive && !thumbnail.classList.contains('is-hidden') && thumbnail.scrollIntoView) {
                thumbnail.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        });
    }

    updateGalleryForVariant(variantId, navigateToImage = true) {
        if (!navigateToImage) return;

        const variant = this.variantData?.find(v => v.id === variantId);
        if (variant?.featured_media_id) {
            const featuredIndex = this.mediaItems.findIndex(img => img.id == variant.featured_media_id);
            if (featuredIndex >= 0) {
                this.switchToImageSmooth(featuredIndex);
                return;
            }
        }

        this.updateActiveThumbnail();
    }

    filterImagesForVariant(variantId) {
        if (!variantId) {
            this.mediaItems = [...this.allMediaItems];
        } else {
            const variantImages = this.allMediaItems.filter(img =>
                img.variants.includes(variantId) || img.variants.length === 0
            );
            this.mediaItems = variantImages.length > 0 ? variantImages : [...this.allMediaItems];
        }

        this.updateThumbnailsDisplay();
    }

    updateThumbnailsDisplay() {
        let visibleIndex = 0;

        this.thumbnails.forEach((thumb, originalIndex) => {
            const media = this.allMediaItems[originalIndex];
            if (!media) return;

            const isVisible = this.mediaItems.some(item => item.id === media.id);

            if (isVisible) {
                this.setThumbnailState(thumb, null);
                thumb.setAttribute('data-thumbnail-index', visibleIndex);
                visibleIndex++;
            } else {
                this.setThumbnailState(thumb, 'hidden');
                thumb.removeAttribute('data-thumbnail-index');
            }
        });
    }

    setupThumbnailClickHandlers() {
        const lightboxTrigger = document.querySelector("[data-lightbox-trigger]");

        if (lightboxTrigger) {
            lightboxTrigger.addEventListener("click", () => this.openLightbox());
        }

        this.thumbnails.forEach((thumbnail) => {
            thumbnail.addEventListener("click", () => {
                const thumbnailIndex = parseInt(thumbnail.getAttribute('data-thumbnail-index'));
                if (!isNaN(thumbnailIndex)) {
                    this.switchToImageSmooth(thumbnailIndex);
                }

                const variantId = thumbnail.dataset.variantId;
                if (variantId) {
                    this.selectVariantById(variantId);
                }
            });
        });
    }

    selectVariantById(variantId) {
        const variant = this.variantData?.find(v => v.id == variantId);
        if (!variant || !variant.options || variant.options.length === 0) return;

        const colorValue = variant.options[0];
        const colorInput = document.querySelector(
            `.color-selector__input[data-option-value="${colorValue}"],
             .variant-option-input[data-option-name="Couleur"][data-option-value="${colorValue}"],
             .variant-option-input[data-option-name="Color"][data-option-value="${colorValue}"]`
        );

        if (colorInput && !colorInput.checked) {
            colorInput.checked = true;
            colorInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    setupMobileGestures() {
        if (!window.matchMedia('(max-width: 768px)').matches) return;

        const gallery = document.querySelector('.product-gallery__main');
        if (!gallery) return;

        let startX = 0, startY = 0;

        gallery.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
        }, { passive: true });

        gallery.addEventListener('touchend', (e) => {
            const endX = e.changedTouches[0].clientX;
            const endY = e.changedTouches[0].clientY;
            const diffX = startX - endX;
            const diffY = startY - endY;

            if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
                if (diffX > 0) this.nextImage();
                else this.previousImage();
            }
        }, { passive: true });
    }

    setupEventListeners() {
        const closeBtn = this.lightbox.querySelector(".lightbox__close");
        const prevBtn = this.lightbox.querySelector(".lightbox__nav--prev");
        const nextBtn = this.lightbox.querySelector(".lightbox__nav--next");

        closeBtn?.addEventListener("click", () => this.closeLightbox());
        prevBtn?.addEventListener("click", () => this.previousImage());
        nextBtn?.addEventListener("click", () => this.nextImage());

        this.lightbox.addEventListener("click", (e) => {
            if (e.target === this.lightbox) this.closeLightbox();
        });

        document.addEventListener("keydown", (e) => {
            if (!this.lightbox.classList.contains("is-active")) return;

            switch (e.key) {
                case "Escape":
                    this.closeLightbox();
                    break;
                case "ArrowLeft":
                    this.previousImage();
                    break;
                case "ArrowRight":
                    this.nextImage();
                    break;
            }
        });
    }

    openLightbox() {
        if (this.mediaItems.length === 0) return;

        this.lightbox.classList.remove("is-closing");
        this.updateLightboxImage();
        this.lightbox.classList.add("is-active");
        document.body.style.overflow = "hidden";
    }

    closeLightbox() {
        this.lightbox.classList.add("is-closing");

        setTimeout(() => {
            this.lightbox.classList.remove("is-active", "is-closing");
            document.body.style.overflow = "";
        }, 200);
    }

    previousImage() {
        const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.mediaItems.length - 1;

        if (this.lightbox.classList.contains("is-active")) {
            this.currentIndex = newIndex;
            this.updateLightboxImage();
        } else {
            this.switchToImageSmooth(newIndex);
        }
    }

    nextImage() {
        const newIndex = this.currentIndex < this.mediaItems.length - 1 ? this.currentIndex + 1 : 0;

        if (this.lightbox.classList.contains("is-active")) {
            this.currentIndex = newIndex;
            this.updateLightboxImage();
        } else {
            this.switchToImageSmooth(newIndex);
        }
    }

    updateLightboxImage() {
        const currentMedia = this.mediaItems[this.currentIndex];
        if (!currentMedia) return;

        this.lightboxImage.src = currentMedia.fullSrc;
        this.lightboxImage.alt = currentMedia.alt;
        this.lightboxCounter.textContent = `${this.currentIndex + 1} / ${this.mediaItems.length}`;

        const navButtons = this.lightbox.querySelectorAll(".lightbox__nav");
        navButtons.forEach((btn) => {
            btn.style.display = this.mediaItems.length > 1 ? "flex" : "none";
        });
    }
}

document.addEventListener("DOMContentLoaded", () => {
    window.productGallery = new ProductMediaLightbox();
});